import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'

function ConsentBody({ format, text }) {
  const s = text == null ? '' : String(text)
  const f = (format == null || String(format).trim() === '') ? 'TEXT' : String(format).trim().toUpperCase()
  if (!s.trim()) return null
  if (f !== 'MARKDOWN') {
    return <span className="whitespace-pre-line">{s}</span>
  }
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      components={{
        p: ({ children }) => <p className="m-0 whitespace-pre-line">{children}</p>,
        ul: ({ children }) => <ul className="m-0 list-disc space-y-1 pl-4">{children}</ul>,
        ol: ({ children }) => <ol className="m-0 list-decimal space-y-1 pl-4">{children}</ol>,
        li: ({ children }) => <li className="m-0">{children}</li>,
        a: ({ children, ...props }) => (
          <a {...props} className="underline" target="_blank" rel="noreferrer">
            {children}
          </a>
        ),
      }}
    >
      {s}
    </ReactMarkdown>
  )
}

/**
 * 카테고리 랜딩 / 공개 소개페이지 등 리드 폼용 통합 동의 UI (체크 1개 + [내용보기] 팝업)
 */
export default function LeadPrivacyConsent({
  collectUse = null,
  thirdParty = null,
  checked,
  onChange,
  accentColor = '#2563eb',
  className = '',
  theme = 'light',
}) {
  const [open, setOpen] = React.useState(false)
  const hasThird = Boolean(thirdParty?.text)
  const summary = hasThird
    ? '개인정보 수집·이용 및 개인정보 제3자 제공에 동의합니다.'
    : '개인정보 수집·이용에 동의합니다.'
  const isDark = String(theme || '').toLowerCase() === 'dark'

  return (
    <>
      <div className={`flex flex-wrap items-start gap-x-2 gap-y-1 text-sm leading-6 ${isDark ? 'text-white/80' : 'text-slate-600'} ${className}`}>
        <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-2">
          <input
            type="checkbox"
            checked={Boolean(checked)}
            onChange={(e) => onChange(Boolean(e.target.checked))}
            className="mt-1 shrink-0"
            required
          />
          <span className="min-w-0">{summary}</span>
        </label>
        <button
          type="button"
          className={`shrink-0 text-sm font-semibold underline underline-offset-2 hover:opacity-90 ${isDark ? 'decoration-white/40' : 'decoration-slate-400'}`}
          style={{ color: isDark ? '#ffffff' : accentColor }}
          onClick={() => setOpen(true)}
        >
          [내용보기]
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <button type="button" className="absolute inset-0 bg-black/45" onClick={() => setOpen(false)} aria-label="닫기" />
          <div
            className="relative flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="lead-privacy-consent-title"
          >
            <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
              <h2 id="lead-privacy-consent-title" className="text-base font-bold text-slate-900">
                개인정보 동의 안내
              </h2>
              <button
                type="button"
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                onClick={() => setOpen(false)}
              >
                닫기
              </button>
            </div>
            <div className="overflow-y-auto px-4 py-4 text-sm text-slate-700">
              <section className="space-y-2">
                <h3 className="text-sm font-bold text-slate-900">1. 개인정보 수집·이용</h3>
                <div className="rounded-lg bg-slate-50 p-3">
                  <ConsentBody format={collectUse?.format} text={collectUse?.text || '내용을 불러오지 못했습니다. 페이지를 새로고침 후 다시 시도해 주세요.'} />
                </div>
              </section>
              {hasThird ? (
                <section className="mt-5 space-y-2">
                  <h3 className="text-sm font-bold text-slate-900">2. 개인정보 제3자 제공</h3>
                  <div className="rounded-lg bg-slate-50 p-3">
                    <ConsentBody format={thirdParty?.format} text={thirdParty.text} />
                  </div>
                </section>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
